import type { CampaignClient } from '@/types/appointments';
import { getCurrencySymbol } from '@/utils/currency';

export type AbsenceSeverity = 'active' | 'warning' | 'danger' | 'critical' | 'lost';

export interface DaysAbsentBucket {
  id: string;
  tierIndex: number;
  label: string;
  shortLabel: string;
  minDays: number;
  maxDays: number | null;
  severity: AbsenceSeverity;
  orderKey: number;
}

const ABSENCE_TIER_NAMES = [
  'Inactivo Reciente',
  'Inactivo en Riesgo',
  'Crítico / En Fuga',
  'Perdido / Desconectado',
];

/**
 * Resolves the exponential tier for days absent relative to base threshold T (inactivityDays).
 * Mathematical Formula:
 *   Tier = floor(log2(daysAbsent / T))
 *   Interval = [T * 2^k, T * 2^(k+1))
 */
export function getDaysAbsentBucket(
  daysAbsent: number | null | undefined,
  threshold: number = 60,
  maxTiers: number = 4
): DaysAbsentBucket {
  const safeDays = Math.max(0, Number(daysAbsent) || 0);
  const T = Math.max(1, Math.round(threshold || 60));

  // Case: active client (under threshold T)
  if (safeDays < T) {
    return {
      id: 'active_on_track',
      tierIndex: 0,
      label: `Al día / Activo (< ${T} días)`,
      shortLabel: `Activo: < ${T}d`,
      minDays: 0,
      maxDays: T,
      severity: 'active',
      orderKey: 0,
    };
  }

  // Geometric scale factor: ratio = days / T >= 1.0
  const ratio = Math.max(1, safeDays / T);
  const rawTier = Math.floor(Math.log2(ratio));
  const tierIndex = Math.min(rawTier, maxTiers - 1);

  const minMultiplier = Math.pow(2, tierIndex);
  const minDays = T * minMultiplier;
  const isLastTier = tierIndex >= maxTiers - 1;
  const maxDays = isLastTier ? null : T * Math.pow(2, tierIndex + 1);

  const tierName = ABSENCE_TIER_NAMES[Math.min(tierIndex, ABSENCE_TIER_NAMES.length - 1)];

  let label: string;
  let shortLabel: string;

  if (isLastTier) {
    label = `Inactividad: ${tierName} (> ${minDays} días)`;
    shortLabel = `${tierName}: > ${minDays}d`;
  } else {
    label = `Inactividad: ${tierName} (${minDays} - ${maxDays} días)`;
    shortLabel = `${tierName}: ${minDays}–${maxDays}d`;
  }

  const severityMap: Record<number, AbsenceSeverity> = {
    0: 'warning',
    1: 'danger',
    2: 'critical',
    3: 'lost',
  };

  return {
    id: `lapsed_tier_${tierIndex + 1}`,
    tierIndex: tierIndex + 1,
    label,
    shortLabel,
    minDays,
    maxDays,
    severity: severityMap[tierIndex] || 'lost',
    orderKey: tierIndex + 1,
  };
}

/**
 * Groups clients exponentially by days absent (recency).
 */
export function groupClientsByDaysAbsent(
  clients: CampaignClient[],
  threshold: number = 60
): Record<string, CampaignClient[]> {
  const groups = new Map<string, { label: string; list: CampaignClient[]; orderKey: number }>();

  for (const client of clients) {
    const bucket = getDaysAbsentBucket(client.daysAbsent, threshold);
    if (!groups.has(bucket.id)) {
      groups.set(bucket.id, {
        label: bucket.label,
        list: [],
        orderKey: bucket.orderKey,
      });
    }
    groups.get(bucket.id)!.list.push(client);
  }

  const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
  const result: Record<string, CampaignClient[]> = {};
  for (const [, grp] of sorted) {
    result[grp.label] = grp.list;
  }
  return result;
}

/**
 * Groups clients dynamically by total sales consumption tiers relative to VIP sales target.
 */
export function groupClientsBySales(
  clients: CampaignClient[],
  currencyCode: string = 'USD',
  vipMinSales: number = 250
): Record<string, CampaignClient[]> {
  const sym = getCurrencySymbol(currencyCode);
  const S = Math.max(50, Math.round(vipMinSales || 250));

  // Exponential tiers based on S: 2S (Ultra VIP), S (VIP), S/2.5 (Frecuente), < S/2.5 (Básico)
  const tierUltra = S * 2;
  const tierVip = S;
  const tierMid = Math.round(S / 2.5);

  const groups = new Map<string, { label: string; list: CampaignClient[]; orderKey: number }>();

  for (const client of clients) {
    const sales = Number(client.totalSales || 0);
    let key = '';
    let label = '';
    let orderKey = 0;

    if (sales >= tierUltra) {
      key = 'ultra_vip';
      label = `Consumo VIP Elite: Más de ${sym}${tierUltra}.00`;
      orderKey = 1;
    } else if (sales >= tierVip) {
      key = 'vip';
      label = `Consumo VIP: ${sym}${tierVip}.00 - ${sym}${tierUltra}.00`;
      orderKey = 2;
    } else if (sales >= tierMid) {
      key = 'regular';
      label = `Consumo Frecuente: ${sym}${tierMid}.00 - ${sym}${tierVip}.00`;
      orderKey = 3;
    } else {
      key = 'starter';
      label = `Consumo Inicial: Menos de ${sym}${tierMid}.00`;
      orderKey = 4;
    }

    if (!groups.has(key)) {
      groups.set(key, { label, list: [], orderKey });
    }
    groups.get(key)!.list.push(client);
  }

  const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
  const result: Record<string, CampaignClient[]> = {};
  for (const [, grp] of sorted) {
    result[grp.label] = grp.list;
  }
  return result;
}

/**
 * Groups clients dynamically by total visits relative to VIP appointment target.
 */
export function groupClientsByVisits(
  clients: CampaignClient[],
  vipMinAppointments: number = 10
): Record<string, CampaignClient[]> {
  const V = Math.max(4, Math.round(vipMinAppointments || 10));

  // Exponential tiers based on V: 2V (Super Frecuente), V (Fiel), V/2 (En Desarrollo), < V/2 (Ocasional)
  const tierSuper = V * 2;
  const tierVip = V;
  const tierMid = Math.max(2, Math.round(V / 2));

  const groups = new Map<string, { label: string; list: CampaignClient[]; orderKey: number }>();

  for (const client of clients) {
    const visits = Number(client.totalAppointments || 0);
    let key = '';
    let label = '';
    let orderKey = 0;

    if (visits >= tierSuper) {
      key = 'visits_super';
      label = `Frecuencia Alta: Más de ${tierSuper} visitas`;
      orderKey = 1;
    } else if (visits >= tierVip) {
      key = 'visits_vip';
      label = `Frecuencia VIP: ${tierVip} a ${tierSuper} visitas`;
      orderKey = 2;
    } else if (visits >= tierMid) {
      key = 'visits_mid';
      label = `Frecuencia Media: ${tierMid} a ${tierVip - 1} visitas`;
      orderKey = 3;
    } else {
      key = 'visits_low';
      label = `Frecuencia Inicial: Menos de ${tierMid} visitas`;
      orderKey = 4;
    }

    if (!groups.has(key)) {
      groups.set(key, { label, list: [], orderKey });
    }
    groups.get(key)!.list.push(client);
  }

  const sorted = [...groups.entries()].sort((a, b) => a[1].orderKey - b[1].orderKey);
  const result: Record<string, CampaignClient[]> = {};
  for (const [, grp] of sorted) {
    result[grp.label] = grp.list;
  }
  return result;
}
